/** Shape of the 403 body the backend sends when a free-tier cap refuses an action. */
export interface FreeTierLimitBody {
    code: 'FREE_TIER_LIMIT';
    limit?: string;
    cap?: number;
    current?: number;
    message?: string;
}

/** Minimal structural shape of an axios-style error this module reads — nothing more. */
interface ApiErrorLike {
    response?: {
        data?: {
            code?: string;
            message?: string;
        } | null;
    };
}

function asApiErrorLike(error: unknown): ApiErrorLike | undefined {
    return typeof error === 'object' && error !== null ? (error as ApiErrorLike) : undefined;
}

/**
 * True for a 403 refused specifically by the free-tier cap (code === "FREE_TIER_LIMIT" on the
 * response body). Keyed strictly off `code`, never the message — the same code covers headcount
 * adds, device-key generation, and module gating, and callers don't need to care which.
 */
export function isFreeTierLimitError(error: unknown): boolean {
    return asApiErrorLike(error)?.response?.data?.code === 'FREE_TIER_LIMIT';
}

/** The server-supplied `message` from a FREE_TIER_LIMIT body, when present. */
export function freeTierLimitMessage(error: unknown): string | undefined {
    const data = asApiErrorLike(error)?.response?.data;
    return typeof data?.message === 'string' && data.message ? data.message : undefined;
}

/**
 * True for the 402 refused by a usage cap (monthly transactions or storage, BLOCK mode). The body is
 * `{ code: "USAGE_LIMIT", limit: "TRANSACTIONS" | "STORAGE", cap, current, message }`. Keyed off `code` only.
 */
export function isUsageLimitError(error: unknown): boolean {
    return asApiErrorLike(error)?.response?.data?.code === 'USAGE_LIMIT';
}

/** The server-supplied `message` from a USAGE_LIMIT body, falling back to a generic sentence. */
export function usageLimitMessage(error: unknown): string {
    const data = asApiErrorLike(error)?.response?.data;
    return typeof data?.message === 'string' && data.message
        ? data.message
        : 'Your monthly usage limit has been reached. Upgrade the plan or switch to pay-as-you-go in Settings.';
}
